import { useEffect, useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { collection, collectionGroup, doc, getDoc, getDocs, setDoc, orderBy, query, where, Timestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { getUserPublicData } from "../services/userServices";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import PostPreviewModal from "../components/PostPreviewModal";

interface ReportOption {
    value: string;
    label: string;
    category?: "legal" | "community";
}

interface ReportStatusOption extends ReportOption {
    color: "blue" | "gray" | "red" | "green" | "orange" | "purple";
}

const STATUS_COLOR_CLASSES: Record<ReportStatusOption["color"], string> = {
    blue: "bg-blue-50 text-blue-800 border-blue-100",
    gray: "bg-gray-100 text-gray-600 border-gray-200",
    red: "bg-red-50 text-red-700 border-red-200",
    green: "bg-green-50 text-green-800 border-green-100",
    orange: "bg-orange-50 text-orange-800 border-orange-100",
    purple: "bg-purple-50 text-purple-800 border-purple-100",
};

interface AppealData {
    note: string;
    submittedAt: Timestamp;
    status: "pending" | "upheld" | "denied";
}

interface ReportDoc {
    id: string;
    reporterId: string;
    reportedUserId: string;
    postId: string;
    reason: string;
    note?: string | null;
    status: string;
    createdAt: Timestamp;
    appeal?: AppealData;
}

interface SocialBlockDetails {
    isBlocked?: boolean;
    blockedReason?: string;
    blockedReasonCategory?: "legal" | "community";
    blockedAt?: Timestamp;
    blockedDuration?: number | null;
    appeal?: AppealData;
}

interface EnrichedReport extends ReportDoc {
    reporterUsername: string;
    reportedUsername: string;
}

interface SuspendedUser {
    userId: string;
    username: string;
    imageUrl?: string;
    block: SocialBlockDetails;
}

const DAY_MS = 86_400_000;

const isSuspensionActive = (b?: SocialBlockDetails | null): boolean => {
    if (!b || !b.isBlocked) return false;
    if (b.blockedDuration == null) return true;
    const startMs = b.blockedAt?.toMillis?.() ?? 0;
    return startMs + b.blockedDuration * DAY_MS > Date.now();
};

const ReportsPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<"reports" | "suspensions">("reports");

    const [reports, setReports] = useState<EnrichedReport[]>([]);
    const [reasonOptions, setReasonOptions] = useState<ReportOption[]>([]);
    const [statusOptions, setStatusOptions] = useState<ReportStatusOption[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [previewingPost, setPreviewingPost] = useState<{ authorId: string; postId: string } | null>(null);

    const [suspendingReport, setSuspendingReport] = useState<EnrichedReport | null>(null);
    const [suspendingUser, setSuspendingUser] = useState<{ userId: string; username: string } | null>(null);
    const [suspendReason, setSuspendReason] = useState<string>("");
    const [suspendDuration, setSuspendDuration] = useState<string>("7");
    const [submittingSuspend, setSubmittingSuspend] = useState(false);

    // Direct user search on the Suspensions tab — restores the ability to
    // find and suspend a user who hasn't necessarily been reported.
    const [userSearchTerm, setUserSearchTerm] = useState("");
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const [userSearchError, setUserSearchError] = useState<string | null>(null);
    const [foundSearchUser, setFoundSearchUser] = useState<SuspendedUser | null>(null);

    // Map of userId -> current socialBlockDetails, fetched fresh alongside
    // reports. Single source of truth for "is this user suspended right
    // now" across both the Reports tab and the Suspensions tab.
    const [blockedUsers, setBlockedUsers] = useState<Map<string, SuspendedUser>>(new Map());
    const [suspensionsLoading, setSuspensionsLoading] = useState(true);
    const [resolvingUserId, setResolvingUserId] = useState<string | null>(null);

    const fetchOptions = async () => {
        try {
            setOptionsLoading(true);
            const functions = getFunctions();
            const getReportOptions = httpsCallable(functions, "getReportOptions");
            const result = await getReportOptions();
            const data = result.data as { reasons: ReportOption[]; statuses: ReportStatusOption[] };
            setReasonOptions(data.reasons ?? []);
            setStatusOptions(data.statuses ?? []);
        } catch (err: any) {
            console.error("Error fetching report options:", err);
            setError(`Failed to fetch report options: ${err.message}`);
        } finally {
            setOptionsLoading(false);
        }
    };

    // socialBlockDetails now lives at users/{userId}/private/socialBlockDetails
    // instead of a field on the main user doc, so this is a collection-group
    // query across every "private" subcollection filtered to isBlocked docs
    // (private/data docs never have that field, so they're naturally excluded).
    // Requires a COLLECTION_GROUP index on private.isBlocked — see
    // firestore.indexes.json in PeakPWA.
    // Powers both the "Suspend user" button state on each report row and
    // the standalone Suspensions tab — always re-run after any admin action
    // so the page reflects real Firestore state, not stale local state.
    const fetchBlockedUsers = async () => {
    try {
        setSuspensionsLoading(true);

        // Get all user IDs (adjust if you already have a lighter-weight users list)
        const usersSnap = await getDocs(collection(db, "users"));

        const map = new Map<string, SuspendedUser>();
        await Promise.all(
            usersSnap.docs.map(async (userDoc) => {
                const userId = userDoc.id;
                try {
                    const blockSnap = await getDoc(doc(db, "users", userId, "private", "socialBlockDetails"));
                    if (!blockSnap.exists()) return;
                    const block = blockSnap.data() as SocialBlockDetails;
                    if (!block.isBlocked) return;

                    const publicData = await getUserPublicData(userId).catch(() => null);
                    map.set(userId, {
                        userId,
                        username: (publicData as any)?.username || userId,
                        imageUrl: (publicData as any)?.imageUrl,
                        block,
                    });
                } catch {
                    // skip users we can't read
                }
            }),
        );
        setBlockedUsers(map);
    } catch (err) {
        console.error("Error fetching blocked users:", err);
        setError(`Failed to fetch suspensions: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
        setSuspensionsLoading(false);
    }
};

    const fetchReports = async () => {
        try {
            setLoading(true);
            setError(null);

            const reportsQuery = query(collection(db, "reports"), orderBy("createdAt", "desc"));
            const snap = await getDocs(reportsQuery);
            const rawReports = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ReportDoc, "id">) }));

            const userCache = new Map<string, string>();
            const resolveUsername = async (userId: string): Promise<string> => {
                if (userCache.has(userId)) return userCache.get(userId)!;
                try {
                    const publicData = await getUserPublicData(userId);
                    const username = publicData?.username || `User ${userId.slice(-4)}`;
                    userCache.set(userId, username);
                    return username;
                } catch {
                    const fallback = `User ${userId.slice(-4)}`;
                    userCache.set(userId, fallback);
                    return fallback;
                }
            };

            const enriched = await Promise.all(
                rawReports.map(async (report) => {
                    const [reporterUsername, reportedUsername] = await Promise.all([
                        resolveUsername(report.reporterId),
                        resolveUsername(report.reportedUserId),
                    ]);
                    return { ...report, reporterUsername, reportedUsername };
                }),
            );

            setReports(enriched);
        } catch (err: any) {
            console.error("Error fetching reports:", err);
            setError(`Failed to fetch reports: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Refetch everything — called on mount and after any admin action so
    // every view always reflects real, current Firestore state.
    const refetchAll = () => {
        fetchOptions();
        fetchReports();
        fetchBlockedUsers();
    };

    useEffect(() => {
        refetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStatusChange = async (report: EnrichedReport, newStatus: string) => {
        if (updatingId) return;

        const previousStatus = report.status;
        setReports((prev) => prev.map((r) => (r.id === report.id ? { ...r, status: newStatus } : r)));
        setUpdatingId(report.id);

        try {
            await setDoc(doc(db, "reports", report.id), { status: newStatus }, { merge: true });
        } catch (err: any) {
            console.error("Error updating report status:", err);
            setError(`Failed to update status: ${err.message}`);
            setReports((prev) => prev.map((r) => (r.id === report.id ? { ...r, status: previousStatus } : r)));
        } finally {
            setUpdatingId(null);
        }
    };

    const handleResolveAppeal = async (report: EnrichedReport, resolution: "upheld" | "denied") => {
        if (updatingId || !report.appeal) return;

        setUpdatingId(report.id);

        const newReportStatus = resolution === "upheld" ? "under_review" : report.status;

        const previousReport = { ...report };
        setReports((prev) =>
            prev.map((r) =>
                r.id === report.id
                    ? { ...r, status: newReportStatus, appeal: { ...r.appeal!, status: resolution } }
                    : r,
            ),
        );

        try {
            await setDoc(
                doc(db, "reports", report.id),
                {
                    status: newReportStatus,
                    appeal: { ...report.appeal, status: resolution },
                },
                { merge: true },
            );
        } catch (err: any) {
            console.error("Error resolving appeal:", err);
            setError(`Failed to resolve appeal: ${err.message}`);
            setReports((prev) => prev.map((r) => (r.id === report.id ? previousReport : r)));
        } finally {
            setUpdatingId(null);
        }
    };

    const openSuspendDialog = (report: EnrichedReport) => {
        setSuspendingReport(report);
        setSuspendDuration("7");
    };

    const openSuspendDialogForUser = (user: { userId: string; username: string }) => {
        setSuspendingUser(user);
        setSuspendReason(reasonOptions[0]?.value ?? "");
        setSuspendDuration("7");
    };

    const closeSuspendDialog = () => {
        setSuspendingReport(null);
        setSuspendingUser(null);
        setSuspendReason("");
    };

    const categoryForReason = (reasonValue: string): "legal" | "community" => {
        const match = reasonOptions.find((r) => r.value === reasonValue);
        return match?.category === "legal" ? "legal" : "community";
    };

    // Handles both suspend flows — from a report (reason comes from the
    // report) and from a direct user search (admin picks the reason).
    const handleConfirmSuspend = async () => {
        const targetUserId = suspendingReport?.reportedUserId ?? suspendingUser?.userId;
        const reason = suspendingReport?.reason ?? suspendReason;
        if (!targetUserId || !reason) return;

        setSubmittingSuspend(true);
        setError(null);

        const duration = suspendDuration === "permanent" ? null : parseInt(suspendDuration, 10);

        try {
            const functions = getFunctions();
            const suspendUser = httpsCallable(functions, "suspendUser");
            await suspendUser({
                userId: targetUserId,
                reason,
                reasonCategory: categoryForReason(reason),
                duration,
            });

            if (suspendingReport) {
                await setDoc(doc(db, "reports", suspendingReport.id), { status: "action_taken" }, { merge: true });
                setReports((prev) =>
                    prev.map((r) => (r.id === suspendingReport!.id ? { ...r, status: "action_taken" } : r)),
                );
            }

            // Re-pull real suspension state rather than guessing it locally.
            await fetchBlockedUsers();
            if (suspendingUser) {
                setFoundSearchUser(null);
                setUserSearchTerm("");
            }
            closeSuspendDialog();
        } catch (err: any) {
            console.error("Error suspending user:", err);
            setError(`Failed to suspend user: ${err.message}`);
        } finally {
            setSubmittingSuspend(false);
        }
    };

    // Mirrors the exact-match username lookup used in PeakPWA's connections
    // search (usernames/{lowercasedUsername} -> { userId, username }).
    const handleUserSearch = async () => {
        const term = userSearchTerm.trim();
        if (!term) {
            setUserSearchError("Please enter a username");
            return;
        }

        setUserSearchLoading(true);
        setUserSearchError(null);
        setFoundSearchUser(null);

        try {
            const usernameSnap = await getDoc(doc(db, "usernames", term.toLowerCase()));
            if (!usernameSnap.exists()) {
                setUserSearchError("User not found");
                return;
            }
            const { userId, username } = usernameSnap.data() as { userId: string; username: string };

            const [userSnap, blockSnap] = await Promise.all([
                getDoc(doc(db, "users", userId)),
                getDoc(doc(db, "users", userId, "private", "socialBlockDetails")),
            ]);
            const userData = (userSnap.data() as any) ?? {};

            setFoundSearchUser({
                userId,
                username: userData.username || username,
                imageUrl: userData.imageUrl,
                block: (blockSnap.data() as SocialBlockDetails) ?? {},
            });
        } catch (err: any) {
            console.error("Error searching for user:", err);
            setUserSearchError(`Failed to search for user: ${err.message}`);
        } finally {
            setUserSearchLoading(false);
        }
    };

    const handleLiftSuspension = async (userId: string) => {
        setResolvingUserId(userId);
        setError(null);
        try {
            const functions = getFunctions();
            const liftSuspension = httpsCallable(functions, "liftSuspension");
            await liftSuspension({ userId });
            await fetchBlockedUsers();
        } catch (err: any) {
            console.error("Error lifting suspension:", err);
            setError(`Failed to lift suspension: ${err.message}`);
        } finally {
            setResolvingUserId(null);
        }
    };

    const handleUpholdSuspensionAppeal = async (userId: string) => {
        setResolvingUserId(userId);
        setError(null);
        try {
            const functions = getFunctions();
            const liftSuspension = httpsCallable(functions, "liftSuspension");
            await liftSuspension({ userId });
            await fetchBlockedUsers();
        } catch (err: any) {
            console.error("Error upholding suspension appeal:", err);
            setError(`Failed to restore access: ${err.message}`);
        } finally {
            setResolvingUserId(null);
        }
    };

    const handleDenySuspensionAppeal = async (user: SuspendedUser) => {
        if (!user.block.appeal) return;
        setResolvingUserId(user.userId);
        setError(null);
        try {
            await setDoc(
                doc(db, "users", user.userId, "private", "socialBlockDetails"),
                {
                    appeal: { ...user.block.appeal, status: "denied" },
                },
                { merge: true },
            );
            await fetchBlockedUsers();
        } catch (err: any) {
            console.error("Error denying suspension appeal:", err);
            setError(`Failed to deny appeal: ${err.message}`);
        } finally {
            setResolvingUserId(null);
        }
    };

    const formatDate = (timestamp?: Timestamp) => {
        if (!timestamp?.toDate) return "—";
        return timestamp.toDate().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getStatusStyle = (statusValue: string) => {
        const match = statusOptions.find((s) => s.value === statusValue);
        return match ? STATUS_COLOR_CLASSES[match.color] : "bg-gray-100 text-gray-600 border-gray-200";
    };

    const getStatusLabel = (statusValue: string) =>
        statusOptions.find((s) => s.value === statusValue)?.label ?? statusValue;

    const getReasonLabel = (reasonValue: string) =>
        reasonOptions.find((r) => r.value === reasonValue)?.label ?? reasonValue;

    const filteredReports =
        statusFilter === "all"
            ? reports
            : statusFilter === "__appeals"
              ? reports.filter((r) => r.appeal?.status === "pending")
              : reports.filter((r) => r.status === statusFilter);

    const statusCounts = reports.reduce<Record<string, number>>((acc, r) => {
        acc[r.status] = (acc[r.status] ?? 0) + 1;
        return acc;
    }, {});

    const pendingSuspensionAppeals = Array.from(blockedUsers.values()).filter(
        (u) => u.block.appeal?.status === "pending",
    );

    const isLoadingAnything = loading || optionsLoading;

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-6 p-6 h-[calc(100vh-64px)] overflow-hidden">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading">Reports</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            User reports and social-feature suspensions.
                        </p>
                    </div>
                    <button
                        onClick={refetchAll}
                        disabled={isLoadingAnything || suspensionsLoading}
                        className="btn btn-sm !h-10 gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ArrowPathIcon
                            className={`w-4 h-4 ${isLoadingAnything || suspensionsLoading ? "animate-spin" : ""}`}
                        />
                        Refresh
                    </button>
                </div>

                {error && (
                    <div className="p-3 text-sm bg-red-50 text-red-500 border border-red-200 rounded-lg">
                        <span>{error}</span>
                    </div>
                )}

                {/* Top-level tabs: Reports vs Suspensions */}
                <div className="flex items-center gap-1 border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab("reports")}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                            activeTab === "reports"
                                ? "border-primary text-primary"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        Reports
                    </button>
                    <button
                        onClick={() => setActiveTab("suspensions")}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                            activeTab === "suspensions"
                                ? "border-primary text-primary"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        Suspensions
                        {pendingSuspensionAppeals.length > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] rounded-full bg-purple-100 text-purple-700">
                                {pendingSuspensionAppeals.length}
                            </span>
                        )}
                    </button>
                </div>

                {activeTab === "reports" && (
                    <>
                        <div className="flex items-center gap-2 border-b border-gray-200 flex-wrap">
                            <button
                                onClick={() => setStatusFilter("all")}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                    statusFilter === "all"
                                        ? "border-primary text-primary"
                                        : "border-transparent text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                All ({reports.length})
                            </button>
                            {statusOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => setStatusFilter(opt.value)}
                                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                        statusFilter === opt.value
                                            ? "border-primary text-primary"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    {opt.label} ({statusCounts[opt.value] ?? 0})
                                </button>
                            ))}
                            <button
                                onClick={() => setStatusFilter("__appeals")}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                    statusFilter === "__appeals"
                                        ? "border-primary text-primary"
                                        : "border-transparent text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                Report Appeals ({reports.filter((r) => r.appeal?.status === "pending").length})
                            </button>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto">
                            {isLoadingAnything || suspensionsLoading ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    Loading reports...
                                </div>
                            ) : filteredReports.length === 0 ? (
                                <div className="flex items-center justify-center h-32 text-gray-400">
                                    No reports{" "}
                                    {statusFilter === "__appeals"
                                        ? "with pending appeals"
                                        : statusFilter !== "all"
                                          ? `with status "${getStatusLabel(statusFilter)}"`
                                          : "yet"}
                                    .
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border overflow-hidden">
                                    <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase">
                                        <div className="col-span-2">Reporter</div>
                                        <div className="col-span-2">Reported User</div>
                                        <div className="col-span-2">Post</div>
                                        <div className="col-span-2">Reason / Note</div>
                                        <div className="col-span-2">Reported On</div>
                                        <div className="col-span-2">Status</div>
                                    </div>
                                    {filteredReports.map((report) => {
                                        const blockedUser = blockedUsers.get(report.reportedUserId);
                                        const suspended = isSuspensionActive(blockedUser?.block);
                                        return (
                                            <div
                                                key={report.id}
                                                className="grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50 items-start transition-colors"
                                            >
                                                <div className="col-span-2">
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {report.reporterUsername}
                                                    </span>
                                                    <span className="block text-[10px] text-gray-400 mt-0.5">
                                                        {report.reporterId.slice(-6)}
                                                    </span>
                                                </div>

                                                <div className="col-span-2">
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {report.reportedUsername}
                                                    </span>
                                                    <span className="block text-[10px] text-gray-400 mt-0.5">
                                                        {report.reportedUserId.slice(-6)}
                                                    </span>
                                                    {suspended && (
                                                        <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] rounded-full bg-red-100 text-red-700">
                                                            Suspended
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="col-span-2">
                                                    <button
                                                        onClick={() =>
                                                            setPreviewingPost({
                                                                authorId: report.reportedUserId,
                                                                postId: report.postId,
                                                            })
                                                        }
                                                        className="text-xs text-blue-700 hover:underline"
                                                    >
                                                        View post
                                                    </button>
                                                    {suspended ? (
                                                        <span className="block text-xs text-gray-400 mt-1">
                                                            Already suspended
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => openSuspendDialog(report)}
                                                            className="block text-xs text-red-600 hover:underline mt-1"
                                                        >
                                                            Suspend user
                                                        </button>
                                                    )}
                                                    <span className="block text-[10px] text-gray-400 mt-0.5 break-all">
                                                        {report.postId.slice(-8)}
                                                    </span>
                                                </div>

                                                <div className="col-span-2">
                                                    <span className="inline-block px-2 py-0.5 text-xs bg-orange-50 text-orange-800 border border-orange-100 rounded-full">
                                                        {getReasonLabel(report.reason)}
                                                    </span>
                                                    {report.note && (
                                                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                                            {report.note}
                                                        </p>
                                                    )}

                                                    {report.appeal && (
                                                        <div className="mt-2 p-2 bg-purple-50 border border-purple-100 rounded-md">
                                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                                <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-700">
                                                                    Report Appeal
                                                                </span>
                                                                <span
                                                                    className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                                        report.appeal.status === "pending"
                                                                            ? "bg-yellow-100 text-yellow-800"
                                                                            : report.appeal.status === "upheld"
                                                                              ? "bg-green-100 text-green-800"
                                                                              : "bg-gray-100 text-gray-600"
                                                                    }`}
                                                                >
                                                                    {report.appeal.status}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-gray-600 line-clamp-2">
                                                                {report.appeal.note}
                                                            </p>

                                                            {report.appeal.status === "pending" && (
                                                                <div className="flex gap-1.5 mt-2">
                                                                    <button
                                                                        onClick={() =>
                                                                            handleResolveAppeal(report, "upheld")
                                                                        }
                                                                        disabled={updatingId === report.id}
                                                                        className="flex-1 text-[10px] font-medium px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                                                    >
                                                                        Uphold &amp; Reopen
                                                                    </button>
                                                                    <button
                                                                        onClick={() =>
                                                                            handleResolveAppeal(report, "denied")
                                                                        }
                                                                        disabled={updatingId === report.id}
                                                                        className="flex-1 text-[10px] font-medium px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                                                                    >
                                                                        Deny
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="col-span-2">
                                                    <span className="text-xs text-gray-500">
                                                        {formatDate(report.createdAt)}
                                                    </span>
                                                </div>

                                                <div className="col-span-2">
                                                    <select
                                                        value={report.status}
                                                        disabled={updatingId === report.id}
                                                        onChange={(e) => handleStatusChange(report, e.target.value)}
                                                        className={`w-full px-2 py-1.5 text-xs font-medium rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 ${getStatusStyle(report.status)}`}
                                                    >
                                                        {!statusOptions.some((s) => s.value === report.status) && (
                                                            <option value={report.status}>{report.status}</option>
                                                        )}
                                                        {statusOptions.map((opt) => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {activeTab === "suspensions" && (
                    <div className="flex-1 min-h-0 overflow-y-auto">
                        <div className="max-w-2xl mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Find a user to suspend
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={userSearchTerm}
                                    onChange={(e) => setUserSearchTerm(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleUserSearch()}
                                    placeholder="Search by username..."
                                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                />
                                <button
                                    onClick={handleUserSearch}
                                    disabled={userSearchLoading}
                                    className="btn btn-sm !h-10 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {userSearchLoading ? "Searching..." : "Search"}
                                </button>
                            </div>

                            {userSearchError && (
                                <p className="text-xs text-red-600 mt-2">{userSearchError}</p>
                            )}

                            {foundSearchUser && (
                                <div className="bg-white rounded-xl border p-4 mt-3 flex items-center gap-3">
                                    {foundSearchUser.imageUrl ? (
                                        <img
                                            src={foundSearchUser.imageUrl}
                                            alt={foundSearchUser.username}
                                            className="w-9 h-9 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600">
                                            {foundSearchUser.username?.charAt(0).toUpperCase() || "?"}
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">
                                            {foundSearchUser.username}
                                        </p>
                                        <p className="text-[10px] text-gray-400">{foundSearchUser.userId}</p>
                                    </div>
                                    {isSuspensionActive(foundSearchUser.block) ? (
                                        <span className="ml-auto text-xs text-gray-400">Already suspended</span>
                                    ) : (
                                        <button
                                            onClick={() =>
                                                openSuspendDialogForUser({
                                                    userId: foundSearchUser.userId,
                                                    username: foundSearchUser.username,
                                                })
                                            }
                                            className="ml-auto px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700"
                                        >
                                            Suspend user
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {suspensionsLoading ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">
                                Loading suspensions...
                            </div>
                        ) : blockedUsers.size === 0 ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">
                                No users are currently suspended.
                            </div>
                        ) : (
                            <div className="space-y-3 max-w-2xl">
                                {Array.from(blockedUsers.values())
                                    // Pending appeals surface first — that's the thing needing action.
                                    .sort((a, b) => {
                                        const aPending = a.block.appeal?.status === "pending" ? 0 : 1;
                                        const bPending = b.block.appeal?.status === "pending" ? 0 : 1;
                                        return aPending - bPending;
                                    })
                                    .map((user) => {
                                        const active = isSuspensionActive(user.block);
                                        const pendingAppeal =
                                            user.block.appeal?.status === "pending" ? user.block.appeal : null;
                                        return (
                                            <div key={user.userId} className="bg-white rounded-xl border p-4">
                                                <div className="flex items-center gap-3 mb-3">
                                                    {user.imageUrl ? (
                                                        <img
                                                            src={user.imageUrl}
                                                            alt={user.username}
                                                            className="w-9 h-9 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600">
                                                            {user.username?.charAt(0).toUpperCase() || "?"}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-medium text-gray-900 text-sm">
                                                            {user.username}
                                                        </p>
                                                        <p className="text-[10px] text-gray-400">{user.userId}</p>
                                                    </div>
                                                    <span
                                                        className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                                            active
                                                                ? "bg-red-100 text-red-700"
                                                                : "bg-gray-100 text-gray-500"
                                                        }`}
                                                    >
                                                        {active ? "Active" : "Expired"}
                                                    </span>
                                                </div>

                                                <div className="text-sm text-gray-700 space-y-0.5 mb-3">
                                                    <p>
                                                        <span className="font-medium">Reason:</span>{" "}
                                                        {getReasonLabel(user.block.blockedReason || "")}
                                                    </p>
                                                    <p>
                                                        <span className="font-medium">Category:</span>{" "}
                                                        {user.block.blockedReasonCategory === "legal"
                                                            ? "Legal basis"
                                                            : "Community Guidelines"}
                                                    </p>
                                                    <p>
                                                        <span className="font-medium">Duration:</span>{" "}
                                                        {user.block.blockedDuration == null
                                                            ? "Permanent"
                                                            : `${user.block.blockedDuration} days`}
                                                    </p>
                                                </div>

                                                {user.block.appeal && (
                                                    <div className="p-2.5 bg-purple-50 border border-purple-100 rounded-md mb-3">
                                                        <div className="flex items-center justify-between gap-2 mb-1">
                                                            <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-700">
                                                                Suspension Appeal
                                                            </span>
                                                            <span
                                                                className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                                    user.block.appeal.status === "pending"
                                                                        ? "bg-yellow-100 text-yellow-800"
                                                                        : user.block.appeal.status === "upheld"
                                                                          ? "bg-green-100 text-green-800"
                                                                          : "bg-gray-100 text-gray-600"
                                                                }`}
                                                            >
                                                                {user.block.appeal.status}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-gray-600">{user.block.appeal.note}</p>

                                                        {pendingAppeal && (
                                                            <div className="flex gap-1.5 mt-2">
                                                                <button
                                                                    onClick={() =>
                                                                        handleUpholdSuspensionAppeal(user.userId)
                                                                    }
                                                                    disabled={resolvingUserId === user.userId}
                                                                    className="flex-1 text-[10px] font-medium px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                                                >
                                                                    Uphold &amp; Restore
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDenySuspensionAppeal(user)}
                                                                    disabled={resolvingUserId === user.userId}
                                                                    className="flex-1 text-[10px] font-medium px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
                                                                >
                                                                    Deny
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <button
                                                    onClick={() => handleLiftSuspension(user.userId)}
                                                    disabled={resolvingUserId === user.userId}
                                                    className="w-full px-3 py-1.5 text-xs rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                                >
                                                    {resolvingUserId === user.userId ? "Lifting..." : "Lift suspension"}
                                                </button>
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </div>
                )}

                {previewingPost && (
                    <PostPreviewModal
                        authorId={previewingPost.authorId}
                        postId={previewingPost.postId}
                        onClose={() => setPreviewingPost(null)}
                    />
                )}

                {(suspendingReport || suspendingUser) && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl max-w-md w-full p-6">
                            <h3 className="text-lg font-heading mb-1">Suspend from social</h3>
                            <p className="text-sm text-gray-500 mb-4">
                                Suspend{" "}
                                <span className="font-medium text-gray-800">
                                    {suspendingReport ? suspendingReport.reportedUsername : suspendingUser?.username}
                                </span>{" "}
                                from Peak's social features.
                            </p>

                            {suspendingReport ? (
                                <div className="text-sm text-gray-700 space-y-1 mb-4">
                                    <p>
                                        <span className="font-medium">Reason:</span>{" "}
                                        {getReasonLabel(suspendingReport.reason)}
                                    </p>
                                    <p>
                                        <span className="font-medium">Category:</span>{" "}
                                        {categoryForReason(suspendingReport.reason) === "legal"
                                            ? "Legal basis"
                                            : "Community Guidelines"}
                                    </p>
                                </div>
                            ) : (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                                    <select
                                        value={suspendReason}
                                        onChange={(e) => setSuspendReason(e.target.value)}
                                        disabled={submittingSuspend}
                                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    >
                                        {reasonOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Category:{" "}
                                        {categoryForReason(suspendReason) === "legal"
                                            ? "Legal basis"
                                            : "Community Guidelines"}
                                    </p>
                                </div>
                            )}

                            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                            <select
                                value={suspendDuration}
                                onChange={(e) => setSuspendDuration(e.target.value)}
                                disabled={submittingSuspend}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50 mb-6"
                            >
                                <option value="1">1 day</option>
                                <option value="3">3 days</option>
                                <option value="7">7 days</option>
                                <option value="14">14 days</option>
                                <option value="30">30 days</option>
                                <option value="permanent">Permanent</option>
                            </select>

                            <div className="flex gap-3">
                                <button
                                    onClick={closeSuspendDialog}
                                    disabled={submittingSuspend}
                                    className="flex-1 px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmSuspend}
                                    disabled={submittingSuspend || (!!suspendingUser && !suspendReason)}
                                    className="flex-1 px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submittingSuspend ? "Suspending..." : "Suspend user"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardWrapper>
    );
};

export default ReportsPage;